import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import flatten from 'lodash/flatten';
import get from 'lodash/get';
import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import orderBy from 'lodash/orderBy';
import reduce from 'lodash/reduce';
import { createAction } from '@reduxjs/toolkit';

import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { langSelector } from 'selectors/lang';
import AxiosClient from 'configurations/api-client';
import { buildApiGetCustomPage } from 'constants/api-paths';
import siteActions from 'actions/site-actions';
import customPageActions from 'actions/custom-page-actions';
import { getErrorMessage } from 'utils/message';
import { buildChildSitePath } from 'constants/route-paths';
import { AVAILABLE_TEMPLATES } from 'constants/custom-page';
import { UPDATE_SITE_THEME } from 'constants/redux-actions';
import { isAdminPath } from 'utils/url';
import { isMobileViewport } from 'utils/responsive';

const useGetCustomPageFromCDN = (customPageId, staticJsonUrl) => {
  const query = useQuery({
    queryKey: ['customPage', customPageId],
    queryFn: async ({ signal }) => {
      if (!staticJsonUrl) {
        throw new Error('No staticJsonUrl provided');
      }

      const response = await fetch(staticJsonUrl, { signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch custom page: ${response.status}`);
      }

      return response.json();
    },
    enabled: !isEmpty(staticJsonUrl) && !!customPageId,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1_000,
    retry: false,
  });

  const { isSuccess, data } = query;

  useQuery({
    queryKey: ['customPageImagesPrefetched', customPageId],
    queryFn: () => prefetchCustomPageImages(get(data, 'contents.0', [])),
    gcTime: 30 * 60 * 1_000,
    staleTime: Infinity,
    enabled: isSuccess,
  });

  return query;
};

export const useGetCustomPageQuery = (customPageId, additionalParams, staticJsonUrl) => {
  const { siteSlug } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();

  const triggerTheme = useTriggerCustomPageTheme();

  const { siteId, loading } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);

  const { loaded: isLoadedSiteLang, selectedLang } = useSelector(langSelector);

  const {
    data: cdnData,
    isSuccess: cdnSuccess,
    isFetching: cdnFetching,
  } = useGetCustomPageFromCDN(customPageId, staticJsonUrl);

  const query = useQuery({
    queryKey: ['customPage', customPageId, selectedLang || 0],
    queryFn: async ({ signal }) => {
      if (!isEmpty(cdnData) && cdnSuccess) {
        const langId = selectedLang || 0;
        const contents = cdnData.contents?.[langId] || cdnData.contents?.[0] || cdnData.contents || [];

        return {
          ...cdnData,
          contents,
        };
      }

      const response = await AxiosClient.get(buildApiGetCustomPage(customPageId), {
        params: merge({}, { siteId }, additionalParams),
        data: null,
        signal,
      });

      return response.data;
    },
    enabled: !loading && !!isLoadedSiteLang && !cdnFetching,
    refetchOnWindowFocus: false,
    staleTime: 10 * 60 * 1_000,
  });

  const { isSuccess, isError, data, error } = query;

  useEffect(() => {
    if (isSuccess) {
      if (!data?.customPage) {
        history.push(flatten([siteSlug ? ['/', siteSlug] : null, data?.slug]).join(''));
      } else if (isEmpty(AVAILABLE_TEMPLATES[data?.template])) {
        dispatch(siteActions.initCustomPageContents(get(data, 'contents', [])));
        dispatch(customPageActions.initializeCustomPageData(omit(data, 'contents')));
      } else {
        dispatch(customPageActions.initializeCustomPageData(data));
      }

      triggerTheme(data?.themeConfiguration);
    }
  }, [data, dispatch, history, isSuccess, siteSlug, triggerTheme]);

  useEffect(() => {
    if (isError) {
      toast.error(getErrorMessage(error.response));
      history.push(buildChildSitePath(siteSlug));
    }
  }, [error, history, isError, siteSlug]);

  useEffect(() => () => dispatch(customPageActions.clearCustomPageData()), [dispatch]);

  return query;
};

export const usePrefetchCustomPage = (customPageId, siteId, staticJsonUrl) => {
  const queryClient = useQueryClient();
  const { loaded: isLoadedSiteLang, selectedLang } = useSelector(langSelector);

  const { data: cdnData, isSuccess: cdnSuccess, isFetching } = useGetCustomPageFromCDN(customPageId, staticJsonUrl);

  useEffect(() => {
    if (customPageId && isLoadedSiteLang && !isFetching) {
      queryClient.prefetchQuery({
        queryKey: ['customPage', customPageId, selectedLang],
        queryFn: async ({ signal }) => {
          if (!isEmpty(cdnData) && cdnSuccess) {
            const langId = selectedLang || 0;
            const contents = cdnData.contents?.[langId] || cdnData.contents?.[0] || cdnData.contents || [];

            return {
              ...cdnData,
              contents,
            };
          }

          if (siteId) {
            const response = await AxiosClient.get(buildApiGetCustomPage(customPageId), {
              params: { siteId },
              signal,
            });
            return response.data;
          }

          throw new Error('No staticJsonUrl or siteId provided');
        },
        staleTime: 10 * 60 * 1_000,
        refetchOnWindowFocus: false,
        retry: false,
      });
    }
  }, [
    customPageId,
    siteId,
    staticJsonUrl,
    queryClient,
    isLoadedSiteLang,
    cdnData,
    cdnSuccess,
    selectedLang,
    isFetching,
  ]);
};

export const useTriggerCustomPageTheme = () => {
  const dispatch = useDispatch();

  const triggerCallback = useCallback(
    (data) => {
      if (isAdminPath() && !isEmpty(data)) {
        dispatch(createAction(UPDATE_SITE_THEME)(data));
      }
    },
    [dispatch],
  );

  return triggerCallback;
};

const extractImageUrls = (contents) => {
  const isMobile = isMobileViewport();
  const sizeThreshold = isMobile ? 153_600 : 307_200; // 200KB for mobile, 200KB for desktop

  const imageData = reduce(
    contents,
    (acc, content) => {
      const size = get(content, 'imageMetadata.originalSize', 0);

      if (size >= sizeThreshold) {
        const defaultImageUrl = get(content, 'imageUrl');
        const mobileUrl = get(content, 'mobileImageUrl', defaultImageUrl);
        const imageUrl = isMobile ? mobileUrl : defaultImageUrl;

        if (!isEmpty(imageUrl)) {
          acc.push({ url: imageUrl, size });
        }
      }

      return acc;
    },
    [],
  );

  const sortedUrls = orderBy(imageData, ['size'], ['desc']);
  return sortedUrls;
};

const prefetchImages = (imageUrls) => {
  if (isEmpty(imageUrls)) {
    return;
  }

  const head = document.head || document.getElementsByTagName('head')[0];

  imageUrls.forEach(({ url }) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    link.as = 'image';
    link.fetchPriority = 'low';
    head.appendChild(link);
  });
};

const prefetchCustomPageImages = (contents) => {
  if (isEmpty(contents)) {
    return;
  }

  try {
    const imageUrls = extractImageUrls(contents);
    prefetchImages(imageUrls);
    return true;
  } catch (_error) {
    return false;
  }
};
